import type { WorkflowGoal } from '@fruitful/types';
import { groupBy, keyBy } from 'lodash';

import type { MembershipDetails } from '../../entities/entityTypes';
import {
    useCreateGoal,
    useCreateGoalGroup,
    useCreateGoalTask,
} from '../../entities/membership/MembershipDetailsEntityProvider';

export const useCreateGoalsFromWorkflowGoals = () => {
    const { mutateAsync: createGoalGroup, isPending: isCreateGroupPending } =
        useCreateGoalGroup();
    const { mutateAsync: createGoal, isPending: isCreateGoalPending } =
        useCreateGoal();
    const { mutateAsync: createGoalTask, isPending: isCreateTaskPending } =
        useCreateGoalTask();

    const create = async (
        membershipDetails: MembershipDetails,
        goals: WorkflowGoal[],
    ) => {
        const {
            id: membershipId,
            allGoalCategories,
            goalGroups,
        } = membershipDetails;
        const categoryMap = keyBy(allGoalCategories, 'description');
        const goalsByCategory = groupBy(goals, 'category');

        await Promise.all(
            Object.entries(goalsByCategory).map(
                async ([category, categoryGoals]) => {
                    const categoryMeta = categoryMap[category];
                    if (categoryMeta == null) {
                        return;
                    }
                    const categoryId = categoryMeta.id;
                    let group = goalGroups.find(
                        (group) => group.goalCategoryId === categoryId,
                    );

                    if (group == null) {
                        group = await createGoalGroup({
                            body: {
                                membershipId,
                                goalCategoryId: categoryId,
                            },
                        });
                    }
                    await Promise.all(
                        categoryGoals.map(async (goal) => {
                            const {
                                tasks,
                                membershipId,
                                category,
                                ...goalWithoutTasks
                            } = goal;

                            const createdGoal = await createGoal({
                                urlParams: {
                                    goalGroupId: group.id,
                                },
                                body: {
                                    ...goalWithoutTasks,
                                },
                            });

                            await Promise.all(
                                (tasks ?? []).map(
                                    async ({ status, ...task }) =>
                                        await createGoalTask({
                                            urlParams: {
                                                goalId: createdGoal.id,
                                            },
                                            body: {
                                                ...task,
                                            },
                                        }),
                                ),
                            );
                        }),
                    );
                },
            ),
        );
    };

    return {
        create,
        isLoading:
            isCreateGroupPending || isCreateGoalPending || isCreateTaskPending,
    };
};
